/* eslint-disable import/no-extraneous-dependencies */
import { useEffect, useState, useRef } from "react";
import Cookies from "js-cookie";

import moment from "moment";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { Box, Checkbox, TextField, IconButton, InputAdornment } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EmailIcon from "@mui/icons-material/Email";
import { Link, useNavigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";

import { createApplication } from "../../../redux/application/actions";
// import { getFilteredUsers } from "../../../redux/users/actions";
import { capitalizeFirstLetter, capitalizeName } from "../../../utilities";
import PrimaryButton from "../../../components/shared/PrimaryButton";
import SecondaryButton from "../../../components/shared/SecondaryButton";
import { getCurrentUserSelector } from "../../../redux/users/selector";
import { Roles } from "../../../utilities/constants";
import { setPreviousPath } from "../../../redux/utilities/actions";
import UserTable from "../../../components/shared/UserTable";
import UserFilter from "../../../components/shared/UserFilter";
import { filterFormFields, filterFormSchema } from "./components/filterDetails";
import { getFilteredUsers } from "../../../redux/users/actions";
import PhoneNumberWithMenu from "../../../components/shared/PhoneNumberWithMenu";

export const ViewButton = ({ id, onClick }) => (
  // eslint-disable-next-line react/destructuring-assignment
  <Link to={`/users/${id}`}>
    <PrimaryButton
      onClick={() => {
        Cookies.remove("isFromDashboard");
        onClick();
      }}>
      View
    </PrimaryButton>
  </Link>
);

ViewButton.propTypes = {
  id: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
};

const CustomCheckbox = ({ checked, onClick, disabled }) => <Checkbox disabled={disabled} checked={checked} onClick={onClick} />;

CustomCheckbox.propTypes = {
  checked: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};

const columns = (dispatch, selectedRecords, handleSelectRecord, location) => [
  {
    id: "id",
    label: "ID",
    props: {
      align: "left",
    },
  },
  {
    id: "select",
    label: "Select",
    type: "button",
    value: (row) => <Checkbox checked={selectedRecords.includes(row.id)} onChange={() => handleSelectRecord(row.id)} />,
    props: {
      align: "left",
    },
  },
  {
    id: "fullName",
    label: "Name",
    value: (row) => {
      const firstName = capitalizeName(row?.firstName) || "";
      const lastName = capitalizeName(row?.lastName) || "";
      return `${firstName} ${lastName}`.trim() || "-";
    },
    props: {
      align: "center",
    },
  },
  {
    id: "email",
    label: "E-mail",
    value: (row) => (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
        <Link
          to={`/users/${row.id}`}
          style={{
            color: "inherit",
            textDecoration: "none",
            transition: "color 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.target.style.color = "#d32f2f";
          }}
          onMouseLeave={(e) => {
            e.target.style.color = "inherit";
          }}
          onClick={() => {
            Cookies.remove("isFromDashboard");
            dispatch(setPreviousPath(`/users${location.search}`));
          }}>
          {row?.email}
        </Link>
        {row?.email && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.location.href = `mailto:${row.email}`;
            }}
            sx={{
              padding: "4px",
              "&:hover": {
                color: "#d32f2f",
                backgroundColor: "rgba(211, 47, 47, 0.08)",
              },
            }}>
            <EmailIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    ),
    props: {
      align: "center",
    },
  },
  {
    id: "mobile",
    label: "Mobile Number",
    value: (row) => <PhoneNumberWithMenu phone={row?.profile?.phone} />,
    props: {
      align: "left",
    },
  },
  {
    id: "createdAt",
    label: "Registration Date",
    value: (row) => capitalizeFirstLetter(moment(row.createdAt).format("DD/MM/YYYY")),
    props: {
      align: "left",
    },
  },
];

const Users = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const lastPayloadRef = useRef(null);
  const currentUser = useSelector(getCurrentUserSelector);
  const filteredUsers = useSelector((state) => state.users.filteredUsers || []);
  const totalFiltered = useSelector((state) => state.users.totalFiltered || []);
  // const promoters = useSelector((state) => state.users.data.filter((user) => user.role === Roles.PROMOTER.value));
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [createdAtStartDate, setCreatedAtStartDate] = useState("");
  const [createdAtEndDate, setCreatedAtEndDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState(null);
  const [orderBy, setOrderBy] = useState(null);
  const [filters, setFilters] = useState([]);
  const [debouncedFilterPayload, setDebouncedFilterPayload] = useState([]);

  const [assignJob, setAssignJob] = useState(() => {
    const stateJob = location.state?.assignJob;
    if (stateJob) {
      return stateJob;
    }
    const params = new URLSearchParams(location.search);
    const jobId = params.get("assignJobId") || params.get("assignJob");
    if (jobId) {
      return {
        id: Number(jobId),
        title: params.get("assignJobTitle") || "",
      };
    }
    return null;
  });
  const [isAssigning, setIsAssigning] = useState(false);

  useEffect(() => {
    if (currentUser.role !== Roles.ADMIN.value) {
      navigate("/jobs");
    }
  }, [currentUser]);

  useEffect(() => {
    if (location.state?.assignJob) {
      setAssignJob(location.state.assignJob);
      return;
    }
    const params = new URLSearchParams(location.search);
    const jobId = params.get("assignJobId") || params.get("assignJob");
    if (jobId) {
      setAssignJob({
        id: Number(jobId),
        title: params.get("assignJobTitle") || "",
      });
    }
  }, [location.state, location.search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilterPayload(filters);
    }, 300);

    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    setPage(0);
  }, [debouncedFilterPayload, createdAtStartDate, createdAtEndDate, debouncedSearch]);

  const applyFilterCookie = Cookies.get("applyFilter");

  let parsedCookieFilter = {};

  try {
    parsedCookieFilter = applyFilterCookie ? JSON.parse(applyFilterCookie) : {};
  } catch (e) {
    parsedCookieFilter = {};
  }

  const DEFAULT_FILTER_DATA = {
    page: 0,
    size: 10,
    registeredFrom: null,
    registeredTo: null,
    firstName: null,
    lastName: null,
    email: null,
    nationality: null,
    education: null,
    role: null,
    referralName: null,
    isAccountVerified: null,
    isEmailVerified: null,
    language: null,
    languageLevel: null,
    jobTitle: null,
    employer: null,
    industry: null,
    wtExperience: null,
    degree: null,
    educationLevel: null,
    institutionName: null,
    country: null,
    city: null,
    sortBy: "",
    sortDirection: "",
    generalSearch: null,
    isAppliedJobs: null,
  };

  const cleanObject = (obj) => {
    const res = {};
    Object.keys(obj || {}).forEach((key) => {
      if (obj[key] !== null && obj[key] !== undefined && obj[key] !== "") {
        res[key] = obj[key];
      }
    });
    return res;
  };

  useEffect(() => {
    const cleanedPayload = cleanObject({
      registeredFrom: createdAtStartDate,
      registeredTo: createdAtEndDate,
      generalSearch: debouncedSearch ? debouncedSearch.toLowerCase() : null,
      page,
      size: rowsPerPage,
      sortBy: orderBy,
      sortDirection: order,
      language: debouncedFilterPayload.languages,
      wtExperience: debouncedFilterPayload.workExperinceWT,
      isAccountVerified: debouncedFilterPayload.isAccountVerified ? true : null,
      isEmailVerified: debouncedFilterPayload.isEmailVerified ? true : null,
      isAppliedJobs: debouncedFilterPayload.jobApplicationsCount ? true : null,
      referralName: debouncedFilterPayload.referral,
      institutionName: debouncedFilterPayload.education,
      jobTitle: debouncedFilterPayload.workExperience,
      ...debouncedFilterPayload,
    });

    const filterData = {
      ...DEFAULT_FILTER_DATA,
      ...cleanedPayload,
      isAccountVerified: cleanedPayload.isAccountVerified ? true : null,
      isEmailVerified: cleanedPayload.isEmailVerified ? true : null,
      isAppliedJobs: cleanedPayload.jobApplicationsCount ? true : null,
    };

    const payloadString = JSON.stringify(filterData);
    if (lastPayloadRef.current === payloadString) return;
    lastPayloadRef.current = payloadString;

    dispatch(getFilteredUsers({ ...filterData, ...parsedCookieFilter }));
  }, [createdAtStartDate, createdAtEndDate, debouncedSearch, page, rowsPerPage, order, orderBy, debouncedFilterPayload]);

  const handleSelectRecord = (id) => {
    setSelectedRecords((prevSelected) => (prevSelected.includes(id) ? prevSelected.filter((recordId) => recordId !== id) : [...prevSelected, id]));
  };

  const exportToCSV = () => {
    const selectedData = filteredUsers.filter((user) => selectedRecords.includes(user.id));
    const csvContent = [
      ["ID", "Name", "E-mail", "Role", "Registration Date"],
      ...selectedData.map((user) => [user.id, `${user.firstName || ""} ${user.lastName || ""}`.trim() || "-", user.email, user.role, moment(user.createdAt).format("DD/MM/YYYY")]),
    ]
      .map((e) => e.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "selected_users.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleAssign = () => {
    if (!assignJob) {
      toast.error("Unable to determine job for assignment.");
      return;
    }

    setIsAssigning(true);
    Promise.all(selectedRecords.map((userId) => dispatch(createApplication({ jobId: assignJob.id, userId })).unwrap()))
      .then(() => {
        toast.success(assignJob.title ? `Selected users have been assigned to ${assignJob.title}.` : "Selected users have been assigned to the job.");
        setSelectedRecords([]);
      })
      .catch((error) => {
        if (Array.isArray(error)) {
          toast.error(error.filter(Boolean).join(", "));
        } else if (typeof error === "string") {
          toast.error(error);
        } else if (error?.message) {
          toast.error(error.message);
        } else {
          toast.error("Failed to assign one or more users. Please try again.");
        }
      })
      .finally(() => {
        setIsAssigning(false);
      });
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {/* Single row with date filters and buttons - aligned with table */}
      <Box
        sx={{
          width: "95%",
          margin: "auto",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          gap: "10px",
          marginBottom: "10px",
        }}>
        {/* Date filters on the left */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            gap: "10px",
            width: { xs: "100%", md: "auto" },
          }}>
          <TextField
            label="Registered From"
            type="date"
            size="small"
            value={createdAtStartDate}
            onChange={(e) => setCreatedAtStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: "100%", sm: "180px" } }}
          />
          <TextField
            label="Registered To"
            type="date"
            size="small"
            value={createdAtEndDate}
            onChange={(e) => setCreatedAtEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: "100%", sm: "180px" } }}
          />
          <IconButton
            onClick={() => {
              setCreatedAtStartDate("");
              setCreatedAtEndDate("");
              setSearchTerm("");
            }}
            disabled={!createdAtStartDate && !createdAtEndDate && !searchTerm}
            sx={{
              borderRadius: "4px",
              alignSelf: { xs: "center", sm: "auto" },
              "&:hover": {
                borderBottom: "2px solid #C02026",
                backgroundColor: "transparent",
              },
            }}>
            <ClearIcon sx={{ color: "#C02026" }} />
          </IconButton>
          <TextField
            label="General search"
            type="text"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, email, city..."
            sx={{ width: { xs: "100%", sm: "280px" } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ color: "#C02026" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Buttons on the right */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            gap: "10px",
            width: { xs: "100%", md: "auto" },
          }}>
          {assignJob && selectedRecords.length > 0 && (
            <PrimaryButton onClick={handleAssign} disabled={isAssigning} sx={{ width: { xs: "100%", sm: "auto" } }}>
              {isAssigning ? "Assigning..." : "Assign"}
            </PrimaryButton>
          )}
          <SecondaryButton onClick={toggleFilters} sx={{ width: { xs: "100%", sm: "auto" } }}>
            {showFilters ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </SecondaryButton>
          <PrimaryButton onClick={exportToCSV} disabled={selectedRecords.length === 0} sx={{ width: { xs: "100%", sm: "auto" } }}>
            <FileDownloadIcon />
          </PrimaryButton>
        </Box>
      </Box>

      <Box sx={{ display: showFilters ? "block" : "none" }}>
        {filteredUsers && (
          <UserFilter
            filterSteps={filterFormFields()}
            filterSchema={filterFormSchema}
            onFilterChange={(updatedFilters) => {
              setFilters(updatedFilters);
            }}
            shouldStartOpen
            hideInternalButton
          />
        )}
      </Box>
      <UserTable
        columns={columns(dispatch, selectedRecords, handleSelectRecord, location)}
        data={filteredUsers || []}
        isLoading={false}
        order={order}
        orderBy={orderBy}
        onSort={(e, property) => {
          setOrder(orderBy === property && order === "asc" ? "desc" : "asc");
          setOrderBy(property);
        }}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        noResultsContent="There are no users."
        totalData={totalFiltered}
      />
    </Box>
  );
};

export default Users;
